import React, { useContext } from 'react';
import _merge from 'lodash/merge';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import { getContext } from 'common';
import { LabelContext } from 'components/Label';
import { TypographyProps } from 'components/Typography';

import LabelContent from '../LabelContent';

const LABEL_CONTENT_CONFIG = {
  required: false
};

export interface CheckboxLabelContentProps extends TypographyProps{
  /** If `true`, the component will be visually disabled. Note: If the `disabled` prop is not set, it will be internally taken from the immediate parent `Label` component. */
  disabled?: boolean;
  /** The label content. */
  children: React.ReactNode | string;
  /** The maximum number of lines before cutting the text. */
  maxLines?: number;
  /** To override the style of the component. */
  style?: React.CSSProperties;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/**  Any other props than the `CheckboxLabelContent` props will be provided to the <Typography /> component. */
const CheckboxLabelContent = React.forwardRef<HTMLElement, CheckboxLabelContentProps>((props: CheckboxLabelContentProps, ref) => getContext(({ theme }) => {
  const {
    disabled,
    theme: themeProp,
    ...otherProps
  } = props;
  const resolvedTheme = _merge({}, theme.CHECKBOX.LABEL, themeProp);
  const labelContext = useContext(LabelContext);
  const resolvedDisabled = props.disabled !== undefined ? disabled : labelContext.disabled;
  return (
    <LabelContent
      ref={ref}
      theme={resolvedTheme}
      {...{
        ...otherProps,
        style: {
          ...Tokens.TYPOGRAPHY.BODY,
          color: resolvedDisabled ? resolvedTheme.disabled.content : resolvedTheme.content,
          marginRight: Tokens.SPACING.SPACING_16,
          marginBottom: Tokens.SPACING.SPACING_12,
          ...props.style
        },
        ...LABEL_CONTENT_CONFIG
      }}
    />
  );
}));

CheckboxLabelContent.displayName = 'CheckboxLabelContent';

CheckboxLabelContent.defaultProps = {
  disabled: undefined, // `disabled`, by default, needs to be undefined to find is it configured by user or not.
  maxLines: 1,
  style: {},
  theme: {}
};

export default CheckboxLabelContent;
